import { colorsPalettes } from "@similarweb/styles";
import { Button, EmptyState, IconButton } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { KeywordsCountDisplay } from "app/pages/digital-marketing/monitor-and-protect/shared/components/keywords-edit/keywords-count-display";
import { campaignWizardTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/campaign-wizard";
import { TruncateTextWithTooltip } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/typography/texts";
import { useTranslation } from "pro-features/components/WithTranslation";

import { CountriesList } from "../components/countries-list";
import { CountriesSearchHeader } from "../components/countries-search-header";
import { useCountriesStep } from "../hooks/use-countries-step";
import { CAMPAIGN_WIZARD_TRANSLATIONS } from "../translations";

interface ICountriesStepProps {
    selectedCountries: number[];
    deduplicatedKeywords: string[];
    onCountryToggle: (countryId: number) => void;
}

interface ICountriesStepHeaderProps {
    currentStep: number;
    totalSteps: number;
}

interface ICountriesStepFooterProps {
    selectedCountries: number[];
    onNext: () => void | Promise<void>;
    selectedDomain: string;
    isLoading?: boolean;
}

export const CountriesStepHeader = ({ currentStep, totalSteps }: ICountriesStepHeaderProps) => {
    return (
        <Stack gap="8px">
            <Text
                fontVariant="input2"
                color={colorsPalettes.carbon["300"]}
            >
                {CAMPAIGN_WIZARD_TRANSLATIONS.common.stepTitle.translate({ currentStep, totalSteps })}
            </Text>
            <Text
                fontVariant="h5_2"
                color={colorsPalettes.carbon["500"]}
            >
                {CAMPAIGN_WIZARD_TRANSLATIONS.countries.header.translate()}
            </Text>
        </Stack>
    );
};

export const CountriesStepFooter = ({
    selectedCountries,
    onNext,
    selectedDomain,
    isLoading,
}: ICountriesStepFooterProps) => {
    return (
        <>
            <Flex
                gap="8px"
                alignItems="center"
            >
                <Text
                    fontVariant="subtitle2"
                    color={colorsPalettes.carbon["400"]}
                >
                    {CAMPAIGN_WIZARD_TRANSLATIONS.countries.monitoringFor.translate()}
                </Text>
                <TruncateTextWithTooltip
                    lineClamp={1}
                    fontVariant="input2"
                    color={colorsPalettes.carbon["400"]}
                >
                    {selectedDomain}
                </TruncateTextWithTooltip>
            </Flex>
            <Flex gap="4px">
                <IconButton
                    iconName="arrow-right"
                    placement="right"
                    onClick={onNext}
                    isLoading={isLoading}
                    showLoaderIcon={false}
                    isDisabled={selectedCountries.length === 0 || isLoading}
                >
                    {CAMPAIGN_WIZARD_TRANSLATIONS.countries.next.translate()}
                </IconButton>
            </Flex>
        </>
    );
};

export const CountriesStep = ({ selectedCountries, deduplicatedKeywords, onCountryToggle }: ICountriesStepProps) => {
    const translate = useTranslation();
    const { selectedCountryIds, selectedCountriesData, filteredCountries, searchQuery, setSearchQuery } =
        useCountriesStep(selectedCountries);

    const handleCountryToggle = (countryId: number) => {
        const isCurrentlySelected = selectedCountryIds.has(countryId);
        if (isCurrentlySelected) {
            campaignWizardTracking.onCountryRemove(countryId);
        } else {
            campaignWizardTracking.onCountryAdd(countryId);
        }
        onCountryToggle(countryId);
    };

    return (
        <Stack
            height="100%"
            gap="16px"
        >
            <CountriesSearchHeader
                searchQuery={searchQuery}
                selectedCount={selectedCountriesData.length}
                onSearchChange={setSearchQuery}
            />
            <Stack
                flex={1}
                overflow="auto"
            >
                <CountriesList
                    emptyState={
                        <Stack
                            gap="16px"
                            paddingY="44px"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <EmptyState
                                title={translate("monitor_and_protect.countries.empty_state.title", { searchQuery })}
                                subtitle={translate("monitor_and_protect.countries.empty_state.subtitle")}
                            />
                            <div>
                                <Button
                                    type="flat"
                                    onClick={() => setSearchQuery("")}
                                >
                                    {translate("monitor_and_protect.countries.empty_state.clear")}
                                </Button>
                            </div>
                        </Stack>
                    }
                    selectedCountriesData={selectedCountriesData}
                    filteredCountries={filteredCountries}
                    selectedCountryIds={selectedCountryIds}
                    onCountryToggle={handleCountryToggle}
                />
            </Stack>
            <KeywordsCountDisplay
                deduplicatedKeywords={deduplicatedKeywords}
                selectedCountries={selectedCountries}
            />
        </Stack>
    );
};
